import { FieldLabel } from 'core/components/common/FieldLabel'
import { FormInput } from 'core/components/common/FormInput'
import { FormSelect } from 'core/components/common/FormSelect'
import { ModalBody } from 'core/components/common/ModalBody'
import { ModalHeader } from 'core/components/common/ModalHeader'
import ModalWrapper from 'core/components/common/ModalWrapper'
import { useExternalMcpFormModal } from 'lauria/hooks/useExternalMcpFormModal'
import { ExternalMcpFormValues } from 'lauria/types/externalMcp'
import React from 'react'
import { useTranslation } from 'react-i18next'

type ExternalMcpFormModalProps = {
  open: boolean
  mode: 'create' | 'edit'
  initialValues?: Pick<ExternalMcpFormValues, 'authMethod' | 'url'>
  onClose: () => void
  onSubmit: (values: ExternalMcpFormValues) => void
}

export const ExternalMcpFormModal: React.FC<ExternalMcpFormModalProps> = ({
  open,
  mode,
  initialValues,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation()
  const {
    register,
    errors,
    submitForm,
    hide,
    authMethodOptions,
    modalTitle,
    submitLabel,
    showSecretField,
  } = useExternalMcpFormModal({
    open,
    mode,
    initialValues,
    onClose,
    onSubmit,
  })

  return (
    <ModalWrapper
      classNames={{ modal: 'modal modal-external-mcp' }}
      onClose={onClose}
      open={open}
      showCloseIcon={false}
    >
      <ModalHeader titleClassName="modal-title mt-[20px]">
        {modalTitle}
      </ModalHeader>
      <ModalBody className="mx-[30px] my-[20px]">
        <form onSubmit={submitForm} className="w-full">
          <div className="flex flex-col gap-y-[10px]">
            <FieldLabel
              label={t('prompts.form.advanced.mcp.modal.authMethodLabel')}
              required
            />
            <FormSelect
              fieldName="authMethod"
              options={authMethodOptions}
              register={register}
              error={errors.authMethod}
              placeholder={t(
                'prompts.form.advanced.mcp.modal.selectPlaceholder'
              )}
              wrapperClassName="w-full"
              selectClassName="w-full form-input pr-30px"
              wrapperErrorClassName="h-[30px]"
              displayDefaultOption
              required
            />

            <FieldLabel
              label={t('prompts.form.advanced.mcp.modal.urlLabel')}
              required
            />
            <FormInput
              fieldName="url"
              register={register}
              error={errors.url}
              wrapperClassName="w-full"
              inputClassName="w-full form-input"
              wrapperErrorClassName="h-[30px]"
            />

            {showSecretField && (
              <>
                <FieldLabel
                  label={t('prompts.form.advanced.mcp.modal.secretLabel')}
                  required
                />
                <FormInput
                  fieldName="secret"
                  register={register}
                  error={errors.secret}
                  wrapperClassName="w-full"
                  inputClassName="w-full form-input"
                  wrapperErrorClassName="h-[30px]"
                />
              </>
            )}
          </div>

          <div className="mt-[30px] flex flex-col md:flex-row md:items-center justify-between">
            <button
              type="button"
              className="small-btn cancel w-full md:w-auto"
              onClick={hide}
            >
              {t('global.form.button.cancel')}
            </button>
            <button
              type="submit"
              className="small-btn default w-full md:w-auto md:ml-[20px]"
            >
              {submitLabel}
            </button>
          </div>
        </form>
      </ModalBody>
    </ModalWrapper>
  )
}
